// File: helpdesk-client-portal/src/context/AuthContext.js
// Authentication Context for Client Portal

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check authentication status on mount
    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('helpdesk_token');
        
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await api.get('/helpdesk/client/verify');
            if (response.data.success) {
                setClient(response.data.client);
                setIsAuthenticated(true);
            } else {
                localStorage.removeItem('helpdesk_token');
                localStorage.removeItem('helpdesk_client');
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            localStorage.removeItem('helpdesk_token');
            localStorage.removeItem('helpdesk_client');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Login function
    const login = async (email, password) => {
        try {
            const response = await api.post('/helpdesk/client/login', { email, password });
            
            if (response.data.success) {
                const { token, client: clientData } = response.data;
                localStorage.setItem('helpdesk_token', token);
                localStorage.setItem('helpdesk_client', JSON.stringify(clientData));
                setClient(clientData);
                setIsAuthenticated(true);
                return { success: true };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            console.error('Login error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Login failed. Please try again.' 
            };
        }
    };

    // Register function
    const register = async (data) => {
        try {
            const response = await api.post('/helpdesk/client/register', data);
            
            if (response.data.success) {
                const { token, client: clientData } = response.data;
                localStorage.setItem('helpdesk_token', token);
                localStorage.setItem('helpdesk_client', JSON.stringify(clientData));
                setClient(clientData);
                setIsAuthenticated(true);
                return { success: true };
            } else {
                return { success: false, message: response.data.message };
            }
        } catch (error) {
            console.error('Register error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Registration failed. Please try again.' 
            };
        }
    };

    // Logout function
    const logout = () => {
        localStorage.removeItem('helpdesk_token');
        localStorage.removeItem('helpdesk_client');
        setClient(null);
        setIsAuthenticated(false);
    };

    // Update client data
    const updateClient = (newData) => {
        setClient(prev => ({ ...prev, ...newData }));
        const stored = JSON.parse(localStorage.getItem('helpdesk_client') || '{}');
        localStorage.setItem('helpdesk_client', JSON.stringify({ ...stored, ...newData }));
    };

    const value = {
        client,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
        updateClient,
        checkAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
